/**
 * The audio engine (doc 18 §1) — one AudioContext, four buses, and the two
 * continuous beds that make a depth sound like a depth.
 *
 * **The unlock is the whole trap.** Browsers refuse WebAudio until a user
 * gesture, so nothing here is created at boot: the context is built on the first
 * click or keypress, which in practice is DESCEND on the title screen. Until
 * then every call is a no-op that costs nothing. A build with no AudioContext at
 * all (a test, an old browser) behaves the same way.
 *
 * Policy — who may speak and how loud — lives in `policy.ts` and is tested
 * without any of this.
 */

import audioJson from '../content/audio.json' with { type: 'json' };
import { DUCK_DB, VoiceBudget, dbToGain, panFor, sliderGain, type Bus } from './policy.ts';
import { durationOf, noiseBuffer, playVoice, type SoundDef } from './voices.ts';

const SOUNDS = audioJson.sounds as unknown as Record<string, SoundDef>;
const BEDS = audioJson.beds as Record<string, { root: number; fifth: number; noiseCutoff: number; swell: number }>;

export type SoundId = keyof typeof audioJson.sounds;

export interface Volumes {
  master: number;
  music: number;
  sfx: number;
  ambience: number;
}

/** Doc 18 §1. Content owns them, because the meta layer needs them too. */
export const DEFAULT_VOLUMES: Volumes = audioJson.defaults;

/** How far the combat bed opens up over the build bed (doc 13 §C's contrast). */
const COMBAT_LIFT = 2.2;
const RAMP = 0.35;

interface Bed {
  gain: GainNode;
  filter: BiquadFilterNode;
  stop(): void;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buses: Record<Bus, GainNode> | null = null;
  private noise: AudioBuffer | null = null;
  private readonly budget = new VoiceBudget();
  private volumes: Volumes = { ...DEFAULT_VOLUMES };
  private ducked = false;
  private muted = false;
  private bed: { music: Bed; ambience: Bed; depth: number } | null = null;
  private combat = false;

  get running(): boolean {
    return this.ctx !== null;
  }

  /**
   * Called from a real user gesture. Safe to call on every one of them — the
   * second call only resumes a context the browser suspended.
   */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor: typeof AudioContext | undefined =
      typeof AudioContext !== 'undefined' ? AudioContext : undefined;
    if (!Ctor) return; // headless, or a browser without WebAudio: stay silent
    const ctx = new Ctor();
    const master = ctx.createGain();
    master.connect(ctx.destination);
    const buses = {
      music: ctx.createGain(),
      sfx: ctx.createGain(),
      ambience: ctx.createGain(),
    } satisfies Record<Bus, GainNode>;
    for (const g of Object.values(buses)) g.connect(master);
    this.ctx = ctx;
    this.master = master;
    this.buses = buses;
    this.noise = noiseBuffer(ctx);
    this.applyVolumes();
  }

  setVolumes(v: Volumes): void {
    this.volumes = { ...v };
    this.applyVolumes();
  }

  /** Doc 18 §1: mute on window blur, paired with the auto-pause (doc 17 §5). */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyVolumes();
  }

  /** Doc 18 §1: ambience −6 dB while a banner or boss announcement is up. */
  setDucked(ducked: boolean): void {
    if (ducked === this.ducked) return;
    this.ducked = ducked;
    this.applyVolumes();
  }

  private applyVolumes(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.buses) return;
    const t = ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : sliderGain(this.volumes.master), t, 0.02);
    this.buses.music.gain.setTargetAtTime(sliderGain(this.volumes.music) * 0.5, t, 0.05);
    this.buses.sfx.gain.setTargetAtTime(sliderGain(this.volumes.sfx), t, 0.02);
    this.buses.ambience.gain.setTargetAtTime(
      sliderGain(this.volumes.ambience) * 0.6 * (this.ducked ? dbToGain(DUCK_DB) : 1),
      t,
      0.15,
    );
  }

  /**
   * Fire a one-shot. `x`/`width` pan it by screen position (doc 17 §4); omit
   * them for anything that happens everywhere, like a UI click.
   */
  play(id: SoundId, opts: { x?: number; width?: number; gain?: number } = {}): void {
    const ctx = this.ctx;
    const buses = this.buses;
    const def = SOUNDS[id];
    if (!ctx || !buses || !def || !this.noise) return;
    const now = performance.now();
    const durMs = durationOf(def) * 1000;
    if (!this.budget.admit(id, def.priority, now, durMs)) return;
    this.budget.evicted.length = 0; // one-shots are short; letting them ring out
    // is cheaper than tracking a node per voice just to cut 200 ms early

    const out = ctx.createGain();
    out.gain.value = def.gain * (opts.gain ?? 1);
    if (opts.x !== undefined && opts.width !== undefined && typeof ctx.createStereoPanner === 'function') {
      const pan = ctx.createStereoPanner();
      pan.pan.value = panFor(opts.x, opts.width);
      out.connect(pan).connect(buses[def.bus]);
    } else {
      out.connect(buses[def.bus]);
    }
    const at = ctx.currentTime + 0.005;
    for (const v of def.voices) playVoice(ctx, out, this.noise, v, at);
  }

  /**
   * The two continuous voices of a depth: a filtered-noise water bed on the
   * ambience bus, and a root-and-fifth drone on the music bus. Doc 13 §C asks
   * for build/combat contrast, and this is it — the same drone, with the floor
   * arriving when the fight does.
   */
  setDepth(depth: number): void {
    const ctx = this.ctx;
    const buses = this.buses;
    if (!ctx || !buses || !this.noise) return;
    if (this.bed?.depth === depth) return;
    this.stopBeds();
    const spec = BEDS[String(depth)] ?? BEDS['1']!;

    // ambience: white noise through a low-pass that breathes
    const water = ctx.createBufferSource();
    water.buffer = this.noise;
    water.loop = true;
    const waterFilter = ctx.createBiquadFilter();
    waterFilter.type = 'lowpass';
    waterFilter.frequency.value = spec.noiseCutoff;
    waterFilter.Q.value = 0.7;
    const waterGain = ctx.createGain();
    waterGain.gain.value = 0.5;
    water.connect(waterFilter).connect(waterGain).connect(buses.ambience);
    const swell = ctx.createOscillator();
    const swellGain = ctx.createGain();
    swell.type = 'sine';
    swell.frequency.value = spec.swell;
    swellGain.gain.value = spec.noiseCutoff * 0.35;
    swell.connect(swellGain).connect(waterFilter.frequency);
    water.start();
    swell.start();

    // music: a root, its fifth, and a sub that only arrives in combat
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.24;
    const droneFilter = ctx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.value = 300;
    droneFilter.Q.value = 0.5;
    droneGain.connect(droneFilter).connect(buses.music);
    const oscs: OscillatorNode[] = [];
    for (const [freq, wave, level] of [
      [spec.root, 'sine', 0.5],
      [spec.fifth, 'sine', 0.28],
      [spec.root * 0.5, 'triangle', 0.22],
    ] as const) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = wave;
      osc.frequency.value = freq;
      g.gain.value = level;
      osc.connect(g).connect(droneGain);
      osc.start();
      oscs.push(osc);
    }

    this.bed = {
      depth,
      ambience: {
        gain: waterGain,
        filter: waterFilter,
        stop: () => {
          water.stop();
          swell.stop();
        },
      },
      music: {
        gain: droneGain,
        filter: droneFilter,
        stop: () => {
          for (const o of oscs) o.stop();
        },
      },
    };
    this.setCombat(this.combat, true);
  }

  /** Doc 13 §C: the contrast IS the music. Build is open; combat has a floor. */
  setCombat(combat: boolean, force = false): void {
    if (combat === this.combat && !force) return;
    this.combat = combat;
    const ctx = this.ctx;
    if (!ctx || !this.bed) return;
    const t = ctx.currentTime;
    this.bed.music.filter.frequency.setTargetAtTime(combat ? 300 * COMBAT_LIFT : 300, t, RAMP);
    this.bed.music.gain.gain.setTargetAtTime(combat ? 0.34 : 0.24, t, RAMP);
    this.bed.ambience.gain.gain.setTargetAtTime(combat ? 0.36 : 0.5, t, RAMP);
  }

  stopBeds(): void {
    if (!this.bed) return;
    try {
      this.bed.music.stop();
      this.bed.ambience.stop();
    } catch {
      // already stopped: a screen swap raced a tab suspend, and neither cares
    }
    this.bed = null;
  }

  /** Leaving the run screen: the beds go, the budget resets, the context stays. */
  leaveRun(): void {
    this.stopBeds();
    this.budget.clear();
    this.setDucked(false);
  }
}

/** One engine for the session — the browser only gives us one context anyway. */
export const audio = new AudioEngine();
